import React from 'react'
import { useTranslation } from 'react-i18next'
import { MessageSquare } from 'lucide-react'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import './CommentMark.scss'

export interface CommentMarkProps {
  /** Lebende Kommentare der Aufgabe (`useCommentCount`); bei `0` steht nichts da. */
  count: number
  /** Öffnet die Aufgaben-Details auf „Kommentare". Ohne: nur Anzeige, kein Knopf. */
  onOpen?: () => void
  /** Für das Maß der Titelzeile (`useCollapsedRowFit`). */
  'data-row-fit'?: string
  /** Platz und Abstand am Einbauort. */
  className?: string
}

/**
 * **Die Marke der Kommentare** an einer Zeile (#183): kleine Sprechblase mit der Zahl daneben,
 * leise und ohne Farbe, nur ab einem Kommentar — an Aufgaben wie an Unteraufgaben.
 *
 * Ein Knopf ist sie nur, wo es Aufgaben-Details gibt, die sie öffnen kann; sonst wäre es ein Knopf,
 * der nichts tut.
 *
 * @example
 * <CommentMark count={useDataSource().useCommentCount(task.id)} onOpen={() => opener.openComments(task.id)} />
 */
export const CommentMark: React.FC<CommentMarkProps> = ({ count, onOpen, 'data-row-fit': rowFit, className }) => {
  const { t } = useTranslation()
  if (count <= 0) return null

  const content = (
    <>
      <MessageSquare size={14} aria-hidden="true" />
      <span className="comment-mark__count">{count}</span>
    </>
  )

  if (!onOpen) {
    return (
      <WithTooltip content={t('taskComments.count', { count })}>
        <span className={`comment-mark${className ? ` ${className}` : ''}`} role="img" aria-label={t('taskComments.count', { count })} data-row-fit={rowFit}>{content}</span>
      </WithTooltip>
    )
  }
  return (
    <WithTooltip content={t('taskComments.count', { count })}>
      <button
        type="button"
        className={`comment-mark comment-mark--button${className ? ` ${className}` : ''}`}
        aria-label={t('taskComments.mark', { count })}
        data-row-fit={rowFit}
        // Der Druck gehört der Marke, nicht der Auswahl, dem Aufklappen oder dem Ziehen der Zeile.
        onPointerDown={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onDoubleClick={e => e.stopPropagation()}
        onClick={e => {
          e.stopPropagation()
          onOpen()
        }}
      >
        {content}
      </button>
    </WithTooltip>
  )
}
